import { useEffect } from "react";
import { createPortal } from "react-dom";
import { SCHEMA_NAMES } from "instantdb/types";
import { createFileRoute } from "@tanstack/react-router";
import { useRouter } from "@tanstack/react-router";
import { ArrowLeft, ChevronRight, Edit2 } from "lucide-react";
import { PATHS } from "@/config/paths";
import { useMobile } from "@/providers/mobileProvider";
import { useMobileNavbar } from "@/providers/mobileNavbarProvider";
import { Button } from "@/components/ui/button";
import { ResourceDropdown } from "@/components/dropdowns/ResourceDropdown";
import { DEFAULT_SYMBOL } from "@/config/constants";
import { NotFound } from "@/not-found";
import { GlobalError } from "@/global-error";
import { Container } from "@/components/ui/container";
import { useHealthArea } from "@/hooks/healthAreas/useHealthArea";
import { HealthAreaGoals } from "@/components/healthAreas/HealthAreaGoals";
import { HealthAreaRoutines } from "@/components/healthAreas/HealthAreaRoutines";
import { CommonIcon } from "@/icons/CommonIcon";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import { Heading } from "@/components/ui/heading";

export const Route = createFileRoute("/dashboard/health-area/$id/")({
  component: HealthAreaDetailPage,
  notFoundComponent: NotFound,
  errorComponent: GlobalError,
});

function HealthAreaDetailPage() {
  const { id: healthAreaId } = Route.useParams();
  const router = useRouter();
  const { isMobile } = useMobile();
  const { setMobileNavbar } = useMobileNavbar();
  const { healthArea, isLoading } = useHealthArea(healthAreaId);
  const { t } = useTranslation();

  // Set up navbar configuration
  useEffect(() => {
    if (isMobile) {
      setMobileNavbar({
        title: `${t(healthArea?.title)}`,
        showBackButton: true,
      });
    }
  }, [isMobile, setMobileNavbar, healthArea]);

  // Handle navigation when healthArea is not found
  useEffect(() => {
    if (!isLoading && (!healthArea || healthArea.slug === undefined)) {
      router.navigate({ to: PATHS.HEALTH_AREAS });
    }
  }, [isLoading, healthArea, router]);

  const goalsTotal = healthArea?.goals?.filter((goal) => {
    if (typeof goal === "string") return false;
    // @ts-ignore - The type system doesn't properly capture the shape of goal
    return goal.aiAccepted || !goal.aiGenerated;
  }).length;

  const routinesTotal = healthArea?.routines?.filter((routine) => {
    if (typeof routine === "string") return false;
    // @ts-ignore - The type system doesn't properly capture the shape of routine
    return routine.aiAccepted || !routine.aiGenerated;
  }).length;

  if (isLoading) return null;

  if (!healthArea || !healthArea.slug) return null;

  return (
    <Container>
      {!isMobile && (
        <div className="col-span-12 flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => router.navigate({ to: PATHS.HEALTH_AREAS })}
          >
            <ArrowLeft />
          </Button>
        </div>
      )}

      <div className={cn("col-span-12 rounded-xl shadow-sm", healthArea.color)}>
        <div className="flex items-start justify-between gap-6 p-6">
          <div className="flex items-start gap-2 md:gap-5">
            <div
              className="flex items-center justify-center text-2xl min-w-12 min-h-12 md:min-w-16 md:min-h-16 text-4xl md:text-5xl focus-visible:ring-0"
              onClick={(e) => e.stopPropagation()}
            >
              {healthArea.symbol && healthArea.symbol.length > 0
                ? healthArea.symbol
                : DEFAULT_SYMBOL}
            </div>
            <div className="flex flex-col gap-3">
              <div>
                <Heading level="h1" size="h5" className="font-semibold text-foreground">
                  {t(healthArea.title)}
                </Heading>
                <p className="text-muted-foreground max-w-2xl">{t(healthArea.description)}</p>
              </div>

              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1.5 bg-primary/5 px-2.5 py-1 rounded-full border border-primary/10">
                  <CommonIcon name="goals" className="h-3.5 w-3.5 text-primary/70" />
                  <span className="text-xs font-medium">
                    {goalsTotal} {goalsTotal !== 1 ? "goals" : "goal"}
                  </span>
                </span>

                <span className="flex items-center gap-1.5 bg-primary/5 px-2.5 py-1 rounded-full border border-primary/10">
                  <CommonIcon name="routines" className="h-3.5 w-3.5 text-primary/70" />
                  <span className="text-xs font-medium">
                    {routinesTotal} {routinesTotal !== 1 ? "routines" : "routine"}
                  </span>
                </span>
              </div>
            </div>
          </div>

          <ResourceDropdown
            align="end"
            side="top"
            className="mt-0"
            slug={SCHEMA_NAMES.healthAreas}
            resource={healthArea}
          />
        </div>
      </div>

      <HealthAreaRoutines healthArea={healthArea} />

      <div className="col-span-12 mb-4 md:mt-0 text-left">
        <Button
          onClick={() =>
            router.navigate({ to: `/dashboard/library/health-area/${healthArea.slug}` })
          }
          variant="link"
          className="text-primary hover:text-primary/80 transition-colors"
        >
          {t("health_areas_page.explore_more_routines")} <ChevronRight />
        </Button>
      </div>

      <HealthAreaGoals healthArea={healthArea} />

      <div className="col-span-12 mb-4 md:mt-0 text-left">
        <Button
          onClick={() =>
            router.navigate({ to: `/dashboard/library/health-area/${healthArea.slug}` })
          }
          variant="link"
          className="text-primary hover:text-primary/80 transition-colors"
        >
          {t("health_areas_page.explore_more_goals")} <ChevronRight />
        </Button>
      </div>

      {/* Create Portal here */}
      {createPortal(
        <div className="fixed bottom-8 right-0 md:right-4 z-50">
          <ResourceDropdown
            slug={SCHEMA_NAMES.healthAreas}
            resource={healthArea}
            trigger={
              <Button
                data-test-id="create-health-area-button"
                size="icon"
                className="absolute -top-[72px] right-4 size-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 bg-primary hover:bg-primary/90 active:shadow-xl active:bg-primary/90"
                aria-label="Edit health area"
                onTouchStart={(e) => e.preventDefault()}
              >
                <Edit2 className="min-w-5 min-h-5" />
              </Button>
            }
          />
        </div>,
        document.body,
      )}
    </Container>
  );
}
